import { ProcessSupervisionChunk } from './types';
import { z } from 'zod';

// A pre-rating strategy takes in a set of chunks and returns an object with result and explanation
// These can then be used to pre-label chunks
export const PreratingStrategyMap: Record<
  string,
  (
    chunk: ProcessSupervisionChunk,
    params: z.infer<
      (typeof PreratingStrategyParamsSchemaMap)[keyof typeof PreratingStrategyParamsSchemaMap]
    >,
  ) => Promise<{ result: string | number | boolean; explanation: string }>
> = {
  // Add your prerating functions here
  // For example:
  // containsKeyword: async (chunk, params) => chunk.content.includes(params.keyword),
  // matchesRegex: async (chunk, params) => chunk.content.match(params.regex),
  containsKeyword: async (
    chunk,
    params: z.infer<(typeof PreratingStrategyParamsSchemaMap)['containsKeyword']>,
  ) => ({
    result: chunk.content.includes(params.keyword),
    explanation: `Chunk ${
      chunk.content.includes(params.keyword) ? 'contains' : 'does not contain'
    } the keyword "${params.keyword}"`,
  }),

  matchesRegex: async (
    chunk,
    params: z.infer<(typeof PreratingStrategyParamsSchemaMap)['matchesRegex']>,
  ) => {
    const matches = chunk.content.match(params.regex);
    return {
      result: !!matches,
      explanation: matches
        ? `Chunk matches the regex "${params.regex}"`
        : `Chunk does not match the regex "${params.regex}"`,
    };
  },
};

// params schema map
export const PreratingStrategyParamsSchemaMap: Record<string, z.ZodSchema> = {
  // Add your prerating function params here
  // For example:
  containsKeyword: z.object({
    keyword: z.string(),
  }),
  matchesRegex: z.object({
    regex: z.string(),
  }),
};

// return value schemas
export const PreratingReturnValueSchemaMap: Record<string, z.ZodSchema> = {
  matchesRegex: z.enum(['true', 'false']),
  containsKeyword: z.enum(['true', 'false']),
};

export const PreratingStrategySchema = z.object({
  ruleName: z.enum(Object.keys(PreratingStrategyMap) as [string, ...string[]]),
  valueToRatingMapping: z.array(
    z.object({
      functionReturnValue: z.string().describe('The value returned by the prerating function'),
      rating: z
        .number()
        .describe('The rating to assign to the chunk if the function returns this value'),
    }),
  ),
  params: z
    .any()
    .describe(
      'The parameters for the prerating function. For example, for the "containsKeyword" rule, this would be the {"keyword": "test"} to search for the word "test".',
    )
    .optional(),
});

export const getPreratedChunks = async (
  chunks: ProcessSupervisionChunk[],
  preratingStrategies: z.infer<typeof PreratingStrategySchema>[] | undefined,
): Promise<ProcessSupervisionChunk[]> => {
  // if there are no prelabeling strategies, return the chunks as is
  if (!preratingStrategies?.length) {
    return chunks;
  }

  const preratedChunks = await Promise.all(
    chunks.map(async (chunk, _index) => {
      for (const strategy of preratingStrategies) {
        const preratingFunction = PreratingStrategyMap[strategy.ruleName];
        if (preratingFunction) {
          const { result, explanation } = await preratingFunction(chunk, strategy.params || {});
          if (result === undefined || result === null) {
            continue;
          }
          const mappingEntry = strategy.valueToRatingMapping.find(
            entry => entry.functionReturnValue === result.toString(),
          );
          if (mappingEntry) {
            return {
              ...chunk,
              rating: mappingEntry.rating,
              prerated: mappingEntry.rating,
              prerating_explanation: explanation,
            };
          }
        }
      }
      return chunk;
    }),
  );

  return preratedChunks;
};
